import React, {
    useCallback, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { matchPath, useHistory, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import SearchIcon from '@mui/icons-material/Search';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import Translator from '%app/js/common/services/translator';
import WhatField from './WhatField';
import WhereField from './WhereField';
import WhenField from './WhenField';
import CategoryField from './CategoryField';
import QuickDateShortcuts from './QuickDateShortcuts';
import useCategories from '../../services/useCategories';
import { parseSearchParams } from '../../services/searchParams';
import { SEARCH_PATH } from '../../config/routes';
import buildSearchUrl from './buildSearchUrl';
import useWhatSearchNavigation from './useWhatSearchNavigation';
import {
    CLEAR_ALL_KEY, LABEL_KEYS, PLACEHOLDER_KEYS, SUBMIT_KEY,
} from './searchPanelKeys';
import SEARCH_SUBMIT_BUTTON_SX from './submitButtonStyles';
import FIELD_SURFACE_SX from './fieldSurfaceSx';
import { CONTENT_MAX_WIDTH } from '../../config/layout';
import { pickForHighContrast, withHighContrastHoverBorder } from '../highContrastSx';

function isOnSearchResultsPage(pathname) {
    if (typeof pathname !== 'string') {
        return false;
    }
    return matchPath(pathname, { path: SEARCH_PATH, exact: true }) !== null;
}

const INITIAL_STATE = Object.freeze({
    what: '',
    where: '',
    wherePlaceId: null,
    dateFrom: '',
    dateTo: '',
    categoryId: null,
});

function deriveStateFromSearch(search) {
    const parsed = parseSearchParams(search);
    return {
        what: typeof parsed.term === 'string' ? parsed.term : '',
        where: typeof parsed.where === 'string' ? parsed.where : '',
        wherePlaceId: typeof parsed.place_id === 'string' ? parsed.place_id : null,
        dateFrom: typeof parsed.date_from === 'string' ? parsed.date_from : '',
        dateTo: typeof parsed.date_to === 'string' ? parsed.date_to : '',
        categoryId: typeof parsed.category === 'number' ? parsed.category : null,
    };
}

const PANEL_SX = {
    bgcolor: 'background.default',
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    width: '100%',
};

const PANEL_INNER_SX = {
    maxWidth: CONTENT_MAX_WIDTH,
    mx: 'auto',
    px: { xs: 0, md: 2 },
    py: { xs: 0, md: 2.5 },
};

const SUBMIT_COLUMN_RESERVED_WIDTH = '180px';

const FIELDS_GRID_SX = {
    display: 'grid',
    gridTemplateColumns: {
        xs: '1fr',
        sm: '1fr 1fr',
        md: `2fr 1.5fr 1.5fr 1.5fr minmax(${SUBMIT_COLUMN_RESERVED_WIDTH}, auto)`,
    },
    gap: 1.5,
    alignItems: 'end',
    px: { xs: 2, md: 0 },
    py: { xs: 2, md: 0 },
};

const CONTROL_SIZE = { xs: 44, md: 38 };

const SUBMIT_WRAPPER_SX = {
    display: 'flex',
    alignItems: 'center',
    alignSelf: { xs: 'stretch', md: 'end' },
    gridColumn: { xs: 'auto', sm: '1 / -1', md: 'auto' },
    gap: 1,
};

const SUBMIT_BUTTON_SX = {
    ...SEARCH_SUBMIT_BUTTON_SX,
    gap: 0.75,
    py: { xs: 1.25, md: 0.75 },
    flex: 1,
};

const CLEAR_ALL_BUTTON_SX = {
    ...FIELD_SURFACE_SX,
    flexShrink: 0,
    width: CONTROL_SIZE,
    height: CONTROL_SIZE,
    color: 'grey.500',
    '&:hover': withHighContrastHoverBorder({
        bgcolor: pickForHighContrast('selection.bg', 'background.default'),
        borderColor: 'primary.main',
        color: 'primary.main',
    }),
};

function isPristine(state) {
    return Object.keys(INITIAL_STATE).every(key => state[key] === INITIAL_STATE[key]);
}

function SearchPanel({ onSubmit }) {
    const history = useHistory();
    const location = useLocation();
    const { categories, loading: categoriesLoading } = useCategories();
    const { goToSuggestedEvent, goToSearchResults } = useWhatSearchNavigation({ onNavigate: onSubmit });
    const [state, setState] = useState(() => deriveStateFromSearch(location.search));
    const stateRef = useRef(state);
    stateRef.current = state;

    useEffect(() => {
        setState(deriveStateFromSearch(location.search));
    }, [location.search]);

    const commitFiltersToUrl = useCallback((override = {}) => {
        if (!isOnSearchResultsPage(location.pathname)) {
            return;
        }
        const nextState = { ...stateRef.current, ...override };
        const nextUrl = buildSearchUrl(nextState);
        const currentUrl = buildSearchUrl(deriveStateFromSearch(location.search));
        if (nextUrl === currentUrl) {
            return;
        }
        history.replace(nextUrl);
    }, [history, location.pathname, location.search]);

    const setField = useCallback((patch) => {
        setState(prev => ({ ...prev, ...patch }));
    }, []);

    const resetState = useCallback(() => {
        setState(INITIAL_STATE);
    }, []);

    const handleClearAll = useCallback(() => {
        resetState();
        commitFiltersToUrl(INITIAL_STATE);
    }, [resetState, commitFiltersToUrl]);

    const handleWhatChange = useCallback(value => setField({ what: value }), [setField]);

    const handleWhereChange = useCallback(
        value => setField({ where: value, wherePlaceId: null }),
        [setField],
    );

    const handleWhereSelect = useCallback((value, placeId) => {
        const nextPlaceId = placeId || null;
        setField({ where: value, wherePlaceId: nextPlaceId });
        commitFiltersToUrl({ where: value, wherePlaceId: nextPlaceId });
    }, [setField, commitFiltersToUrl]);

    const handleWhereCommit = useCallback((value) => {
        commitFiltersToUrl({ where: value });
    }, [commitFiltersToUrl]);

    const handleCategoryChange = useCallback((value) => {
        setField({ categoryId: value });
        commitFiltersToUrl({ categoryId: value });
    }, [setField, commitFiltersToUrl]);

    const handleWhenChange = useCallback(({ dateFrom, dateTo }) => {
        setField({ dateFrom, dateTo });
        commitFiltersToUrl({ dateFrom, dateTo });
    }, [setField, commitFiltersToUrl]);

    const navigateToSearchResults = useCallback(() => {
        goToSearchResults(stateRef.current);
    }, [goToSearchResults]);

    const handleSubmit = useCallback((event) => {
        event.preventDefault();
        navigateToSearchResults();
    }, [navigateToSearchResults]);

    const handleWhatSelect = useCallback((suggestion) => {
        if (!suggestion || !suggestion.slug) {
            return;
        }
        resetState();
        goToSuggestedEvent(suggestion);
    }, [goToSuggestedEvent, resetState]);

    const pristine = isPristine(state);
    const clearAllLabel = Translator.trans(CLEAR_ALL_KEY);

    return (
        <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            data-testid="search-panel"
            sx={PANEL_SX}
        >
            <Box sx={PANEL_INNER_SX}>
                <Box data-testid="search-panel-fields-grid" sx={FIELDS_GRID_SX}>
                    <WhatField
                        label={Translator.trans(LABEL_KEYS.what)}
                        placeholder={Translator.trans(PLACEHOLDER_KEYS.what)}
                        value={state.what}
                        onChange={handleWhatChange}
                        onSelectSuggestion={handleWhatSelect}
                        onShowAllResults={navigateToSearchResults}
                    />
                    <WhenField
                        label={Translator.trans(LABEL_KEYS.when)}
                        icon={<CalendarTodayIcon fontSize="small" />}
                        placeholder={Translator.trans(PLACEHOLDER_KEYS.when)}
                        dateFrom={state.dateFrom}
                        dateTo={state.dateTo}
                        onChange={handleWhenChange}
                    />
                    <WhereField
                        label={Translator.trans(LABEL_KEYS.where)}
                        placeholder={Translator.trans(PLACEHOLDER_KEYS.where)}
                        value={state.where}
                        onChange={handleWhereChange}
                        onSelect={handleWhereSelect}
                        onCommit={handleWhereCommit}
                    />
                    <CategoryField
                        label={Translator.trans(LABEL_KEYS.category)}
                        placeholder={Translator.trans(PLACEHOLDER_KEYS.category)}
                        value={state.categoryId}
                        onChange={handleCategoryChange}
                        categories={categories}
                        disabled={categoriesLoading}
                    />
                    <Box data-testid="search-panel-submit-wrapper" sx={SUBMIT_WRAPPER_SX}>
                        {!pristine && (
                            <Tooltip title={clearAllLabel} enterDelay={400}>
                                <IconButton
                                    type="button"
                                    onClick={handleClearAll}
                                    aria-label={clearAllLabel}
                                    data-testid="search-panel-clear-all"
                                    sx={CLEAR_ALL_BUTTON_SX}
                                >
                                    <RestartAltIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                        <Button
                            type="submit"
                            variant="contained"
                            startIcon={<SearchIcon fontSize="small" />}
                            data-testid="search-panel-submit"
                            sx={SUBMIT_BUTTON_SX}
                        >
                            {Translator.trans(SUBMIT_KEY)}
                        </Button>
                    </Box>
                </Box>
                <QuickDateShortcuts
                    onSelectRange={handleWhenChange}
                    dateFrom={state.dateFrom}
                    dateTo={state.dateTo}
                />
            </Box>
        </Box>
    );
}

SearchPanel.propTypes = {
    onSubmit: PropTypes.func,
};

export default SearchPanel;
